/* GET /clients/{slug}/wikipedia — the live Wikimedia facts plane.
 *
 * Types mirror the deterministic connector's record (service/tools/wikipedia.py).
 * Every subresource carries an explicit status; the workroom reads those to
 * render honest "unavailable" / "partial" states rather than a silent absence.
 * The connector never fabricates: a failed subresource's article field is
 * `null`, and an incomplete pageviews window reports its REAL span.
 */
import { request } from './client.ts';

export type SubresourceStatus =
  | 'available'
  | 'empty'
  | 'partial'
  | 'unavailable'
  | 'not-implemented';

export interface WpError {
  code: string;
  [k: string]: unknown;
}

export interface WpSubresource {
  status: SubresourceStatus;
  error: WpError | null;
  fetched?: number;
  truncated?: boolean;
}

export interface WpPageviewsSubresource {
  status: SubresourceStatus;
  error: WpError | null;
  requestedDays: number;
  daysFetched: number;
  daysMissing: number;
  gaps: string[];
  duplicateDays: string[];
  outOfRangeDays: string[];
}

export interface WpSection {
  title: string;
  level: number;
  bytes: number;
  shareOfBody: number;
}

export interface WpCitation {
  url: string;
  domain: string | null;
  refCount: number;
  /** dead-link check is not wired yet — always null; render as "not checked". */
  dead: boolean | null;
  /** reliability/role classification is not wired yet — always null. */
  kind: string | null;
}

export interface WpRevision {
  revisionId: number | null;
  parentId: number | null;
  timestamp: string | null;
  user: string | null;
  registered: boolean;
  comment: string;
}

export interface WpEditors {
  registered: number;
  anon: number;
  spa: number | null;
  reverts: number;
  editWar: boolean | null;
  recent: WpRevision[];
}

export interface WpPageviews {
  series: Array<{ date: string; views: number }>;
  spikes: Array<{ date: string; views: number }>;
}

export interface WpArticle {
  site: string;
  language: string;
  title: string;
  canonicalUrl: string;
  pageId: number | null;
  revisionId: number | null;
  revisionTs: string | null;
  protection: Array<{ type?: string; level?: string; expiry?: string }> | null;
  qualityClass: string | null;
  lede: { text: string; htmlAnchor: string } | null;
  sections: WpSection[];
  undueWeight: unknown | null;
  citations: WpCitation[] | null;
  unsourcedClaims: unknown | null;
  talk: unknown | null;
  editors: WpEditors;
  pageviews: WpPageviews | null;
  receipts: Array<Record<string, unknown>>;
}

export interface WpArticleCoverage {
  site: string;
  title: string;
  revisionsFetched: number;
  revisionsTruncated: boolean;
  citationsFetched: number;
  citationsTruncated: boolean;
  contributorsFetched: number;
  contributorsTruncated: boolean;
  talkThreadsOmitted: number;
  langlinksTruncated: boolean;
  subresources: {
    info: WpSubresource;
    revisions: WpSubresource;
    citations: WpSubresource;
    contributors: WpSubresource;
    langlinks: WpSubresource;
    talk: WpSubresource;
    pageviews: WpPageviewsSubresource;
    [k: string]: WpSubresource | WpPageviewsSubresource;
  };
}

export interface WpRecord {
  articles: WpArticle[];
  commons: unknown | null;
  fetchedAt: string;
  source: string;
  coverage: {
    languageEditionsAvailable: number;
    languageEditionsFetched: number;
    languageEditionsFailed: number;
    languageEditionsOmitted: number;
    ceilings: Record<string, number>;
    articles: WpArticleCoverage[];
    subresources: Record<string, WpSubresource>;
  };
}

export interface WpResponse {
  available: boolean;
  reason?: string | null;
  wikipedia?: WpRecord;
}

export interface WpFetchOpts {
  revisions?: number;
  editions?: number;
  talk?: number;
}

/** GET /clients/{slug}/wikipedia?article=... — one live fetch per workroom
 * open. No client-side memo: the connector itself caches complete records for
 * six hours and re-attempts partial ones, so a repeated open is cheap upstream
 * and always reflects the connector's current completeness. */
export function fetchClientWikipedia(
  slug: string,
  article: string,
  opts: WpFetchOpts = {},
): Promise<WpResponse> {
  const q = new URLSearchParams({ article });
  if (opts.revisions != null) q.set('revisions', String(opts.revisions));
  if (opts.editions != null) q.set('editions', String(opts.editions));
  if (opts.talk != null) q.set('talk', String(opts.talk));
  return request<WpResponse>(`/clients/${encodeURIComponent(slug)}/wikipedia?${q}`);
}
